"use client";

import React, { useState, useCallback, useEffect } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import initialState from "@/lib/StaticData/searchFormData";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import { Text } from "@lmn/design-system";

const FilterWrapper = styled.div`
  width: 100%;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  justify-content: space-between;
`;

const RatingButton = styled.button<{ $active: boolean }>`
  background-color: ${({ $active, theme }) =>
    $active
      ? theme.kallisto.color.background.interactive.backgroundPrimaryDefault
      : theme.kallisto.color.background.static.backgroundPage};
  border: 1px solid;
  border-color: ${({ $active, theme }) =>
    $active
      ? theme.kallisto.color.background.interactive.backgroundPrimaryDefault
      : theme.kallisto.color.background.static.backgroundDisabled};
  cursor: pointer;
  border-radius: 50%;
  height: 2.5rem;
  width: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $active, theme }) =>
    $active
      ? theme.kallisto.color.text.static.textBrandContrastPrimary
      : theme.kallisto.color.text.static.textPrimary};
  &:hover {
    border-color: ${({ theme }) =>
      theme.kallisto.color.background.interactive.backgroundPrimaryDefault};
  }

  & * {
    color: inherit;
  }
`;

interface RecommendationFilterProps {
  delayedUpdate?: boolean;
}

const RecommendationFilter: React.FC<RecommendationFilterProps> = ({
  delayedUpdate = false,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilter } = useFilterState();
  const minRecommendation = initialState.minRecommendation;

  // Initialize state from either URL params or temp filters based on delayedUpdate
  const [selectedMinRecommendation, setSelectedMinRecommendation] = useState(
    () => {
      return delayedUpdate
        ? Number(tempFilters.get("minRecommendation")) || 0
        : Number(searchParams.get("minRecommendation")) || 0;
    }
  );

  // Sync with URL changes
  useEffect(() => {
    setSelectedMinRecommendation(
      Number(searchParams.get("minRecommendation")) || 0
    );
  }, [searchParams]);

  const handleRecommendationChange = useCallback(
    (value: number) => {
      const newValue = value === selectedMinRecommendation ? 0 : value;
      setSelectedMinRecommendation(newValue);

      const filterValue = newValue > 0 ? newValue.toString() : null;

      if (delayedUpdate) {
        // Update temp filters for mobile dialog
        updateTempFilter("minRecommendation", filterValue);
      } else {
        // Immediate URL update for desktop view
        updateFilter("minRecommendation", filterValue);
      }
    },
    [selectedMinRecommendation, updateFilter, updateTempFilter, delayedUpdate]
  );

  const handleKeyDown = (event: React.KeyboardEvent, value: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleRecommendationChange(value);
    }
  };

  return (
    <FilterWrapper data-testid="recommendationFilter">
      <span className="sr-only" id="recommendation-rating-label">
        Filter by recommendation rating
      </span>

      {minRecommendation.allIds.map((val) => (
        <RatingButton
          key={`${val}-rating-button`}
          $active={selectedMinRecommendation === val}
          onClick={() => handleRecommendationChange(val)}
          onKeyDown={(event) => handleKeyDown(event, val)}
          tabIndex={0}
          aria-pressed={selectedMinRecommendation === val}
          aria-label={`Filter by ${val}% or higher recommendation`}
          role="button"
        >
          <Text variant="BodySmall">{val}%</Text>
        </RatingButton>
      ))}
    </FilterWrapper>
  );
};

export default RecommendationFilter;
